// File: src/pages/transporters/TransporterListCompact.jsx
// ✅ Same change as before:
// - Add PDF + EXCEL download buttons (no dropdown)
// - Uses exportDataToPDF from ../import/Downloader (CSV-based like your other pages)
// - Keeps your existing XLSX export logic
// - Everything else stays same style/behavior

import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Input, Button, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaFilePdf, FaFileExcel } from "react-icons/fa";
import swal from "sweetalert";

// --- your helpers/APIs/perm check (paths based on your structure) ---
import { _Get, _Delete } from "../import/ApiEndPoint/ApiCalling";
import { CheckPermission } from "../import/CheckPermission";
import {
  Create_Transporter_List,
  Delete_Transporter_List,
} from "../import/ApiEndPoint/Api";

const injectOnce = () => {
  if (document.getElementById("tr-list-inline-css")) return;
  const s = document.createElement("style");
  s.id = "tr-list-inline-css";
  s.textContent = `
    .tr-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .tr-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .tr-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .tr-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:1000px; }
    .tr-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; text-align:center; white-space:nowrap;
    }
    .tr-table tbody td{
      padding:8px 10px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:13px; text-align:center;
    }
    .tr-table tbody tr:hover{ background:#f9fbff; cursor:pointer; }
    .actions{ display:flex; justify-content:center; align-items:center; gap:8px }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; }
    .chip-edit{ background:#d46f10; }
    .chip-del{ background:#ec1809; }
    .chip-eye{ background:#39cccc; }
    .dl-menu{ position:relative }
    .tag{ display:inline-block; background:#eef2ff; color:#1f2937; border:1px solid #e5e7eb; padding:2px 8px; border-radius:999px; font-size:12px; margin:2px; }
    .exp-row{ background:#fcfcff; }
    .exp-box{ text-align:left; padding:10px 12px; }
    .exp-title{ font-weight:700; margin-bottom:6px; color:#111827; }
    .exp-grid{ display:grid; grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr; gap:8px; }
    .exp-item{ border:1px solid #e5e7eb; border-radius:10px; padding:8px; background:white; }
    .exp-h{ font-size:12px; color:#6b7280; margin-bottom:4px; text-transform:uppercase; letter-spacing:.03em;}
    .exp-v{ font-size:13px; color:#111827; }

    /* ===== NEW: row-wise service locations table ===== */
    .exp-table{ width:100%; border-collapse:collapse; table-layout:auto; }
    .exp-table thead th{
      background:#f8fafc; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; white-space:nowrap; text-align:left;
    }
    .exp-table tbody td{
      padding:8px 10px; border-bottom:1px solid #e5e7eb; vertical-align:middle; color:#0f172a; font-size:13px; text-align:left;
    }

    /* ✅ download buttons */
    .tr-dl-btn{
      height:32px;
      border-radius:10px;
      border:1px solid #e5e7eb;
      background:#ffffff;
      font-weight:800;
      text-transform:uppercase;
      padding:0 10px;
      display:inline-flex;
      align-items:center;
      gap:8px;
      box-shadow: 0 8px 16px rgba(0,0,0,0.04);
      cursor:pointer;
    }
    .tr-dl-btn:hover{
      box-shadow: 0 12px 22px rgba(0,0,0,0.08);
      transform: translateY(-1px);
    }
  `;
  document.head.appendChild(s);
};

const PAGE_SIZE = 100;

const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const fmtDate = (iso) => (iso ? String(iso).split("T")[0] : "");

export default function TransporterListCompact() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: true,
    Download: true,
  });

  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => injectOnce(), []);

  const reload = async () => {
    const userinfo = JSON.parse(localStorage.getItem("userData") || "{}");
    const db = userinfo?.database || userinfo?.db || "";

    // permissions
    const inside = CheckPermission("Create Transporter"); // your old page name for perms
    if (inside && typeof inside === "object") {
      setPermissions((prev) => ({ ...prev, ...inside }));
    }

    // fetch list
    const res = await _Get(Create_Transporter_List, db);
    const list = Array.isArray(res?.Transporter) ? res.Transporter : [];
    setRows([...list].reverse());
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await reload();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Pre-compute searchable text once per API response.
  const searchableRows = useMemo(
    () =>
      rows.map((row) => ({
        row,
        searchText: [
          row?.companyName,
          row?.name,
          row?.email,
          row?.contactNumber,
          row?.pincode,
          row?.city,
          row?.state,
          row?.status,
          row?.id,
          fmtDate(row?.createdAt),
          fmtDate(row?.updatedAt),
        ]
          .map(toStr)
          .join(" ")
          .toLowerCase(),
      })),
    [rows],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;

    return searchableRows
      .filter((item) => item.searchText.includes(term))
      .map((item) => item.row);
  }, [rows, search, searchableRows]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pagedRows = useMemo(
    () => filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filtered, safePage],
  );

  useEffect(() => setPage(1), [search]);

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDelete = async (id) => {
    if (!permissions?.Delete) return;
    const choice = await swal("Warning", "Sure You Want to Delete it", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    });
    if (choice === "delete") {
      try {
        await _Delete(Delete_Transporter_List, id);
        setRows((prev) => prev.filter((x) => x?._id !== id));
      } catch (e) {
        swal("Error", "Could not delete transporter", "error");
      }
    }
  };

  /* ---------------- Export helpers ---------------- */
  const buildExportRows = () => {
    return filtered.map((t, i) => ({
      "#": i + 1,
      ID: toStr(t?.id),
      "Transporter Name": toStr(t?.companyName),
      "Incharge Name": toStr(t?.name),
      Email: toStr(t?.email),
      "Contact No": toStr(t?.contactNumber),
      Pincode: toStr(t?.pincode),
      City: toStr(t?.city),
      State: toStr(t?.state),
      Status: toStr(t?.status),
      "Created Date": fmtDate(t?.createdAt),
      "Updated Date": fmtDate(t?.updatedAt),
    }));
  };

  const downloadXLSX = async () => {
    const XLSXModule = await import("xlsx");
    const XLSX = XLSXModule.default || XLSXModule;

    const data = buildExportRows();
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transporters");
    XLSX.writeFile(wb, "TransporterList.xlsx");
  };

  const downloadPDF = async () => {
    const data = buildExportRows();
    const headers = data.length ? Object.keys(data[0]) : [];
    const esc = (v) => `"${toStr(v).replace(/"/g, '""')}"`;

    const csv =
      headers.join(",") +
      "\r\n" +
      data.map((row) => headers.map((h) => esc(row[h])).join(",")).join("\r\n");

    const { exportDataToPDF } = await import("../import/Downloader");
    await exportDataToPDF(csv, "TransporterList");
  };

  return (
    <>
      {/* ===== Toolbar ===== */}
      <Row className="tr-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="tr-card"
            style={{ padding: 8, background: "#f4f7f6" }}
          >
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Transporter List{" "}
                <span className="count-pill">{filtered.length}</span>
              </div>

              <div style={{ flex: "1 1 260px" }} />

              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  className="input-color"
                  placeholder="Search transporter..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              {permissions?.Create && (
                <Button
                  style={{
                    backgroundColor: "rgb(8, 91, 245)",
                    color: "white",
                    fontWeight: 600,
                    height: 32,
                    textTransform: "uppercase",
                  }}
                  onClick={() => navigate(`/app/transporters/create/0`)}
                >
                  <FaPlus size={12} /> Transporter
                </Button>
              )}

              {/* ✅ PDF + EXCEL buttons */}
              {permissions?.Download && (
                <>
                  <button
                    type="button"
                    className="tr-dl-btn"
                    onClick={downloadPDF}
                    title="Download PDF"
                  >
                    <FaFilePdf size={16} />
                    PDF
                  </button>
                  <button
                    type="button"
                    className="tr-dl-btn"
                    onClick={downloadXLSX}
                    title="Download Excel"
                  >
                    <FaFileExcel size={16} />
                    EXCEL
                  </button>
                </>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== Table ===== */}
      <Row>
        <Col sm="12">
          <Card className="tr-card" style={{ padding: 8 }}>
            {loading ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                <Spinner size="sm" color="primary" /> Loading transporters...
              </div>
            ) : filtered.length === 0 ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No transporters found.
              </div>
            ) : (
              <div className="tr-table-wrap">
                <table className="tr-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>ID</th>
                      <th>TRANSPORTER NAME</th>
                      <th>INCHARGE</th>
                      <th>CONTACT</th>
                      <th>EMAIL</th>
                      <th>STATE</th>
                      <th>CITY</th>
                      <th>PINCODE</th>
                      <th>STATUS</th>
                      <th>CREATED</th>
                      <th>UPDATED</th>
                    </tr>
                  </thead>

                  <tbody>
                    {pagedRows.map((t, i) => {
                      const isOpen = expanded.has(t?._id);
                      return (
                        <React.Fragment key={t?._id || i}>
                          <tr onClick={() => toggleExpand(t?._id)}>
                            <td>{(safePage - 1) * PAGE_SIZE + i + 1}</td>
                            <td>{toStr(t?.id)}</td>
                            <td
                              style={{ textAlign: "left", color: "#755cf7" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/app/transporters/view/${t?._id}`);
                              }}
                            >
                              {toStr(t?.companyName)}
                            </td>
                            <td>{toStr(t?.name)}</td>
                            <td>{toStr(t?.contactNumber)}</td>
                            <td style={{ textAlign: "left" }}>
                              {toStr(t?.email)}
                            </td>
                            <td>{toStr(t?.state)}</td>
                            <td>{toStr(t?.city)}</td>
                            <td>{toStr(t?.pincode)}</td>
                            <td>{toStr(t?.status)}</td>
                            <td>{fmtDate(t?.createdAt)}</td>
                            <td>{fmtDate(t?.updatedAt)}</td>
                          </tr>

                          {/* Expandable row */}
                          {isOpen && (
                            <tr className="exp-row">
                              <td colSpan={12} className="exp-box">
                                <div className="exp-title">
                                  Service Locations
                                </div>

                                {Array.isArray(t?.serviceArea) &&
                                t.serviceArea.length ? (
                                  <div
                                    className="tr-table-wrap"
                                    style={{ border: "none", borderRadius: 0 }}
                                  >
                                    <table className="exp-table">
                                      <thead>
                                        <tr>
                                          <th>#</th>
                                          <th>Branch Manager</th>
                                          <th>Contact</th>
                                          <th>Address</th>
                                          <th>City</th>
                                          <th>State</th>
                                          <th>Pincode</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {t.serviceArea.map((sa, idx) => (
                                          <tr key={idx}>
                                            <td
                                              style={{
                                                textAlign: "center",
                                                width: 50,
                                              }}
                                            >
                                              {idx + 1}
                                            </td>
                                            <td>{toStr(sa?.BMName)}</td>
                                            <td>{toStr(sa?.contactNumber)}</td>
                                            <td>{toStr(sa?.address)}</td>
                                            <td>{toStr(sa?.city)}</td>
                                            <td>{toStr(sa?.state)}</td>
                                            <td>{toStr(sa?.pincode)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <div style={{ color: "#6b7280" }}>
                                    No service locations added.
                                  </div>
                                )}

                                {/* Optional delete button inside expand */}
                                {permissions?.Delete && (
                                  <div style={{ marginTop: 10 }}>
                                    <Button
                                      color="danger"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(t?._id);
                                      }}
                                    >
                                      Delete this transporter
                                    </Button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && filtered.length > PAGE_SIZE && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  paddingTop: 12,
                }}
              >
                <Button
                  size="sm"
                  disabled={safePage <= 1}
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                >
                  Previous
                </Button>
                <span style={{ fontSize: 13, fontWeight: 700 }}>
                  {(safePage - 1) * PAGE_SIZE + 1}-
                  {Math.min(safePage * PAGE_SIZE, filtered.length)} of{" "}
                  {filtered.length} · Page {safePage} of {pageCount}
                </span>
                <Button
                  size="sm"
                  disabled={safePage >= pageCount}
                  onClick={() =>
                    setPage((prev) => Math.min(pageCount, prev + 1))
                  }
                >
                  Next
                </Button>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
}
